import { useParams, Link } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { useGuild } from '../hooks/useGuild';
import { useGuildInvites } from '../hooks/useGuildInvites';
import { useGuildBans } from '../hooks/useGuildBans';
import { GuildSettingsForm } from '../components/GuildSettingsForm';
import { GuildInviteManager } from '../components/GuildInviteManager';
import { GuildBanList } from '../components/GuildBanList';

export function GuildManagementPage() {
  const { guildId } = useParams<{ guildId: string }>();
  const { guild, loading, error, update } = useGuild(guildId);
  const {
    invites,
    loading: invitesLoading,
    create: createInvite,
    revoke: revokeInvite,
  } = useGuildInvites(guildId);
  const {
    bans,
    loading: bansLoading,
    error: bansError,
    unban: handleUnban,
  } = useGuildBans(guildId);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !guild) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error || 'Guild not found'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!guild.myRole || !['owner', 'admin'].includes(guild.myRole)) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">You don't have permission to manage this guild.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to={`/a/arche_guilds/${guild.guildId}`}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to {guild.name}
        </Link>
        <h1 className="text-2xl font-semibold">Manage Guild</h1>
      </div>

      <Tabs defaultValue="settings">
        <TabsList>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="invites">Invites</TabsTrigger>
          <TabsTrigger value="bans">Bans</TabsTrigger>
        </TabsList>

        <TabsContent value="settings">
          <Card>
            <CardContent className="pt-6">
              <GuildSettingsForm guild={guild} onSave={update} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invites">
          <Card>
            <CardContent className="pt-6">
              <GuildInviteManager
                invites={invites}
                loading={invitesLoading}
                onCreate={createInvite}
                onRevoke={revokeInvite}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bans">
          <Card>
            <CardContent className="pt-6">
              {bansError ? (
                <p className="text-sm text-destructive">{bansError}</p>
              ) : (
                <GuildBanList bans={bans} loading={bansLoading} onUnban={handleUnban} />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
